"use client";

// Chapitre « Vendre » : l'écran de résultat de l'outil « Combien facturer ».
// Le curseur fait glisser « Ce que vous voulez garder par mois » de
// 2 000 € à 3 000 € et tout se recalcule, comme dans l'outil, jusqu'à la
// note sur la TVA, qui change de cas en chemin (franchise, puis TVA au
// 1er janvier, puis TVA immédiate).
//
// Les montants ne sont PAS écrits en dur : ils sortent de
// lib/tools/dayRate.js, le module de l'outil lui-même. La démonstration ne
// peut donc jamais afficher un tarif que l'outil ne donnerait pas (activité
// libérale, cinq jours par semaine, cinq semaines sans facturer, au barème,
// seul, sans frais : les réglages par défaut de l'outil).

import { useEffect, useRef, useState } from "react";
import { AnimatePresence, animate, motion, useMotionValueEvent } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import { tarifJournalier } from "@/lib/tools/dayRate";
import { useSceneTime } from "../demoClock";
import { glide, useTarget } from "../DemoCursor";
import Icon from "../icons";
import { BrandLogo } from "../sceneParts";

const T = {
    grab: 1300,
    release: 2950,
};

const SLIDER = { min: 500, max: 8000, step: 100 };
const FROM = 2000;
const TO = 3000;
const position = (value) => (value - SLIDER.min) / (SLIDER.max - SLIDER.min);

export const script = {
    duration: 6900,
    still: 3400,
    cursor: [
        { at: T.grab, target: "rate.track", ax: position(FROM), grab: true, move: 900 },
        { at: T.release, target: "rate.track", ax: position(TO), release: true, move: T.release - T.grab },
    ],
};

const PARAMETERS = { activite: "bnc", joursParSemaine: 5, semainesConges: 5 };
const cache = new Map();

function compute(netMensuel) {
    if (!cache.has(netMensuel)) {
        cache.set(
            netMensuel,
            tarifJournalier({ ...PARAMETERS, netMensuel, annee: new Date().getUTCFullYear() }),
        );
    }
    return cache.get(netMensuel);
}

// La valeur du réglage, qui suit la main du curseur sur la même courbe que
// lui, par pas de 100 € comme le vrai curseur.
function useSliderValue() {
    const time = useSceneTime();
    const valueAt = (t) => {
        const progress = Math.max(0, Math.min(1, (t - T.grab) / (T.release - T.grab)));
        const raw = FROM + (TO - FROM) * glide(progress);
        return Math.round(raw / SLIDER.step) * SLIDER.step;
    };
    const [value, setValue] = useState(() => valueAt(time.get()));
    const valueRef = useRef(value);

    useMotionValueEvent(time, "change", (t) => {
        const next = valueAt(t);
        if (next !== valueRef.current) {
            valueRef.current = next;
            setValue(next);
        }
    });

    return value;
}

// Un montant qui glisse vers sa nouvelle valeur au lieu de sauter.
function useTween(target) {
    const [shown, setShown] = useState(target);
    const shownRef = useRef(target);

    useEffect(() => {
        const controls = animate(shownRef.current, target, {
            duration: 0.28,
            ease: "easeOut",
            onUpdate: (latest) => {
                shownRef.current = latest;
                setShown(latest);
            },
        });
        return () => controls.stop();
    }, [target]);

    return shown;
}

function Amount({ value, format, className }) {
    const shown = useTween(value);
    return <span className={`tabular-nums ${className ?? ""}`}>{format(Math.round(shown))}</span>;
}

function Slider({ value, compact }) {
    const t = useTranslations();
    const trackRef = useTarget("rate.track");
    const format = useEuros();

    return (
        <div>
            <div className="flex items-baseline justify-between gap-3">
                <span className={`font-medium text-gray-700 dark:text-grayDark-200 ${compact ? "text-[12px]" : "text-[13px]"}`}>
                    {t("showcase_rate_goal")}
                </span>
                <span className={`font-bold tabular-nums text-gray-900 dark:text-white ${compact ? "text-[14px]" : "text-[16px]"}`}>
                    {format(value)}
                </span>
            </div>
            <div ref={trackRef} className="relative mt-3 flex h-5 items-center">
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-grayDark-700">
                    <div className="h-full bg-primary" style={{ width: `${position(value) * 100}%` }} />
                </div>
                <span
                    className="absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-primary bg-white shadow-md dark:bg-bgDark-800"
                    style={{ left: `${position(value) * 100}%` }}
                />
            </div>
            <p className={`mb-0 mt-3 text-gray-400 dark:text-grayDark-500 ${compact ? "text-[10.5px]" : "text-[12px]"}`}>
                {t("showcase_rate_assumptions")}
            </p>
        </div>
    );
}

function useEuros() {
    const locale = useLocale();
    return (value) =>
        new Intl.NumberFormat(locale, {
            style: "currency",
            currency: "EUR",
            maximumFractionDigits: 0,
        }).format(value);
}

// La note TVA de l'outil, dans l'encadré bleu des explications.
function VatNote({ value }) {
    const t = useTranslations();
    const format = useEuros();
    const { tva } = compute(value);
    const text = {
        franchise: t("showcase_rate_vat_franchise", { base: format(tva.base) }),
        janvier: t("showcase_rate_vat_january", { base: format(tva.base) }),
        immediate: t("showcase_rate_vat_immediate", { threshold: format(tva.majore) }),
    }[tva.statut];

    return (
        <div className="flex gap-2.5 rounded-xl border border-blue-200/70 bg-blue-50/60 p-4 text-[12.5px] leading-relaxed text-blue-900 dark:border-blue-400/20 dark:bg-blue-400/10 dark:text-blue-100">
            <Icon name="info" className="mt-0.5 h-4 w-4 shrink-0 text-blue-500 dark:text-blue-300" />
            <AnimatePresence mode="wait" initial={false}>
                <motion.p
                    key={tva.statut}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="mb-0"
                >
                    {text}
                </motion.p>
            </AnimatePresence>
        </div>
    );
}

function Line({ label, amount, dot, compact }) {
    const format = useEuros();
    return (
        <div
            className={`flex items-center justify-between gap-3 border-b border-gray-100 last:border-b-0 dark:border-grayDark-800 ${
                compact ? "py-1.5 text-[11.5px]" : "py-2.5 text-[13px]"
            }`}
        >
            <span className="flex min-w-0 items-center gap-2.5 text-gray-600 dark:text-grayDark-300">
                <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />
                <span className="truncate">{label}</span>
            </span>
            <Amount value={amount} format={format} className="font-semibold text-gray-900 dark:text-white" />
        </div>
    );
}

function Result({ value, compact }) {
    const t = useTranslations();
    const format = useEuros();
    const result = compute(value);

    return (
        <div className={compact ? "space-y-2.5" : "space-y-4"}>
            <div className={`rounded-xl border border-gray-200/70 dark:border-white/10 ${compact ? "px-3.5 py-3" : "p-5"}`}>
                <p className="mb-1 text-xs text-gray-500 dark:text-grayDark-400">{t("showcase_rate_charge")}</p>
                <p className={`mb-0 font-bold text-gray-900 dark:text-white ${compact ? "text-[24px]" : "text-[32px]"}`}>
                    <Amount value={result.tarifJour} format={format} />{" "}
                    <span className="text-base font-semibold text-gray-500 dark:text-grayDark-400">
                        {t("showcase_rate_per_day")}
                    </span>
                </p>
                <p className={`mb-0 mt-1 text-gray-600 dark:text-grayDark-300 ${compact ? "text-[10.5px] leading-snug" : "text-[13px]"}`}>
                    {t("showcase_rate_detail", {
                        days: result.jours,
                        revenue: format(result.chiffreAffaires),
                        monthly: format(result.chiffreAffaires / 12),
                    })}
                </p>
            </div>

            <div className={compact ? "px-1" : "px-1"}>
                <Line label={t("showcase_rate_contributions")} amount={result.cotisations} dot="bg-primary" compact={compact} />
                <Line label={t("showcase_rate_tax")} amount={result.impot} dot="bg-amber-500" compact={compact} />
                <Line label={t("showcase_rate_training")} amount={result.cfp} dot="bg-gray-400 dark:bg-grayDark-500" compact={compact} />
            </div>

            <div className={`rounded-xl border border-green-600/40 bg-green-600/[0.06] ${compact ? "px-3.5 py-2.5" : "p-5"}`}>
                <p className="mb-1 text-xs text-gray-500 dark:text-grayDark-400">{t("showcase_rate_left")}</p>
                <p className={`mb-0 font-bold text-green-700 dark:text-green-400 ${compact ? "text-[20px]" : "text-[28px]"}`}>
                    <Amount value={result.net} format={format} />
                </p>
                <p className={`mb-0 mt-0.5 text-gray-600 dark:text-grayDark-300 ${compact ? "text-[11px]" : "text-[13px]"}`}>
                    {t("showcase_rate_left_detail", { amount: format(value) })}
                </p>
            </div>
        </div>
    );
}

function ToolTitle({ compact }) {
    const t = useTranslations();
    return (
        <div>
            <span className="inline-flex items-center gap-2 text-[12px] font-semibold text-primary">
                <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary/10">
                    <Icon name="sell" className="h-3.5 w-3.5" />
                </span>
                {t("showcase_rate_tool")}
            </span>
            <p className={`mb-0 font-bold text-gray-900 dark:text-white ${compact ? "mt-2 text-[19px]" : "mt-3 text-[28px] leading-tight"}`}>
                {t("showcase_rate_title")}
            </p>
            {!compact && (
                <p className="mb-0 mt-2 text-[14px] leading-relaxed text-gray-500 dark:text-grayDark-400">
                    {t("showcase_rate_subtitle")}
                </p>
            )}
        </div>
    );
}

export default function RateScene({ compact }) {
    const t = useTranslations();
    const value = useSliderValue();

    if (compact) {
        return (
            <div className="flex h-full flex-col bg-white dark:bg-bgDark-800">
                <header className="flex h-12 shrink-0 items-center justify-between border-b border-gray-100 px-4 dark:border-grayDark-800">
                    <BrandLogo className="h-4 w-[86px]" />
                    <Icon name="menu" className="h-5 w-5 text-gray-500 dark:text-grayDark-400" />
                </header>
                <div className="space-y-3.5 px-4 pt-3.5">
                    <ToolTitle compact />
                    <Slider value={value} compact />
                    <Result value={value} compact />
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col bg-white dark:bg-bgDark-800">
            <header className="flex h-14 shrink-0 items-center gap-8 border-b border-gray-100 px-6 dark:border-grayDark-800">
                <BrandLogo className="h-5 w-[104px]" />
                <nav className="flex gap-6 text-[13px] text-gray-500 dark:text-grayDark-400">
                    <span>{t("layout.SideHeader_courses")}</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{t("layout.SideHeader_toolbox")}</span>
                    <span>{t("layout.SideHeader_articles")}</span>
                </nav>
                <span className="ml-auto flex h-8 w-8 items-center justify-center rounded-full bg-[#1f4e4a] text-[13px] font-semibold text-white">
                    L
                </span>
            </header>

            <div className="grid min-h-0 flex-1 grid-cols-[1fr_440px] gap-12 px-12 pt-9">
                <div className="space-y-8">
                    <ToolTitle />
                    <Slider value={value} />
                    <VatNote value={value} />
                </div>
                <Result value={value} />
            </div>
        </div>
    );
}
